import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getAllUsers, 
  upgradeToAdmin, 
  upgradeToPremium, 
  deleteUserAccount, 
  suspendUser, 
  unsuspendUser, 
  downgradeFromPremium,
  getUserActivityStats,
  searchUsers,
  logAdminAction,
  addSystemAnnouncement
} from '../../services/firebase';
import { UserProfile, UserStatistics } from '../../types/user';
import { DocumentSnapshot } from 'firebase/firestore';
import styles from './AdminPanel.module.css';
import { MdDashboard, MdPeople, MdAnnouncement, MdSettings, MdAnalytics, MdHistory, MdAppRegistration } from 'react-icons/md';

// Alt bileşenler
import UserManagement from './components/UserManagement';
import Dashboard from './components/Dashboard';
import Announcements from './components/Announcements';
import Settings from './components/Settings';
import Analytics from './components/Analytics';
import ActivityLogs from './components/ActivityLogs';
import CityManagement from './components/CityManagement';

type AdminTab = 'dashboard' | 'users' | 'announcements' | 'settings' | 'analytics' | 'logs' | 'cities';

function AdminPanel() {
  const { isAdmin, isLoggedIn, userProfile } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info', text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [userStats, setUserStats] = useState<UserStatistics | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    hasMore: false
  });
  const lastDocRef = useRef<DocumentSnapshot | null>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    if (!isAdmin) {
      navigate('/');
      return;
    }

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Temel kullanıcı verilerini getir
        const allUsers = await getAllUsers();
        setUsers(allUsers);
        
        // Kullanıcı istatistiklerini getir
        const stats = await getUserActivityStats();
        setUserStats({
          ...stats,
          usersByRole: [
            { role: 'user', count: stats.totalUsers - stats.adminUsers },
            { role: 'admin', count: stats.adminUsers }
          ],
          usersByMonth: [
            { month: 'Ocak', count: Math.floor(Math.random() * 100) },
            { month: 'Şubat', count: Math.floor(Math.random() * 100) },
            { month: 'Mart', count: Math.floor(Math.random() * 100) },
            { month: 'Nisan', count: Math.floor(Math.random() * 100) },
            { month: 'Mayıs', count: stats.newUsersLast30Days }
          ]
        });
        
        // Admin işlemini kaydet
        if (userProfile) {
          await logAdminAction(userProfile.uid, 'admin_panel_access', { timestamp: new Date().toISOString() });
        }
        
      } catch (err) {
        console.error("Veriler alınırken hata:", err);
        setError("Veri yüklenirken bir hata oluştu. Lütfen sayfayı yenileyip tekrar deneyin.");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [isAdmin, isLoggedIn, navigate, userProfile]);

  const handleMakeAdmin = async (userId: string) => {
    try {
      setMessage(null);
      await upgradeToAdmin(userId);
      
      // Admin işlemini kaydet
      if (userProfile) {
        await logAdminAction(userProfile.uid, 'upgrade_to_admin', { targetUserId: userId });
      }
      
      // Kullanıcı listesini güncelle
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === userId 
            ? { ...user, role: 'admin' } 
            : user
        )
      );
      
      setMessage({ type: 'success', text: 'Kullanıcı yönetici olarak atandı.' });
    } catch (err) {
      console.error("Yönetici atama hatası:", err);
      setMessage({ type: 'error', text: 'Yönetici atanırken bir hata oluştu.' });
    }
  };

  const handleMakePremium = async (userId: string) => {
    try {
      setMessage(null);
      await upgradeToPremium(userId);
      
      // Admin işlemini kaydet
      if (userProfile) {
        await logAdminAction(userProfile.uid, 'upgrade_to_premium', { targetUserId: userId });
      }
      
      // Kullanıcı listesini güncelle
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === userId 
            ? { ...user, isPremium: true, premiumSince: new Date().toISOString() } 
            : user
        )
      );
      
      setMessage({ type: 'success', text: 'Kullanıcı premium üyeliğe yükseltildi.' });
    } catch (err) {
      console.error("Premium üyelik hatası:", err);
      setMessage({ type: 'error', text: 'Premium üyelik atanırken bir hata oluştu.' });
    }
  };
  
  const handleRemovePremium = async (userId: string) => {
    try {
      setMessage(null);
      await downgradeFromPremium(userId);
      
      // Admin işlemini kaydet
      if (userProfile) {
        await logAdminAction(userProfile.uid, 'downgrade_from_premium', { targetUserId: userId });
      }
      
      // Kullanıcı listesini güncelle
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === userId 
            ? { ...user, isPremium: false, premiumSince: undefined } 
            : user
        )
      );
      
      setMessage({ type: 'success', text: 'Kullanıcının premium üyeliği kaldırıldı.' });
    } catch (err) {
      console.error("Premium üyelik kaldırma hatası:", err);
      setMessage({ type: 'error', text: 'Premium üyelik kaldırılırken bir hata oluştu.' });
    }
  };
  
  const handleSuspendUser = async (userId: string, reason: string) => {
    try {
      setMessage(null);
      await suspendUser(userId, reason);
      
      // Admin işlemini kaydet
      if (userProfile) {
        await logAdminAction(userProfile.uid, 'suspend_user', { targetUserId: userId, reason });
      }
      
      // Kullanıcı listesini güncelle
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === userId 
            ? { 
                ...user, 
                suspended: true, 
                suspensionReason: reason,
                suspendedAt: new Date().toISOString() 
              } 
            : user
        )
      );
      
      setMessage({ type: 'success', text: 'Kullanıcı askıya alındı.' });
    } catch (err) {
      console.error("Kullanıcı askıya alma hatası:", err);
      setMessage({ type: 'error', text: 'Kullanıcı askıya alınırken bir hata oluştu.' });
    }
  };
  
  const handleUnsuspendUser = async (userId: string) => {
    try {
      setMessage(null);
      await unsuspendUser(userId);
      
      // Admin işlemini kaydet
      if (userProfile) {
        await logAdminAction(userProfile.uid, 'unsuspend_user', { targetUserId: userId });
      }
      
      // Kullanıcı listesini güncelle
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.uid === userId 
            ? { 
                ...user, 
                suspended: false, 
                suspensionReason: null,
                suspendedAt: null
              } 
            : user
        )
      );
      
      setMessage({ type: 'success', text: 'Kullanıcı askıdan çıkarıldı.' });
    } catch (err) {
      console.error("Kullanıcı askıdan çıkarma hatası:", err);
      setMessage({ type: 'error', text: 'Kullanıcı askıdan çıkarılırken bir hata oluştu.' });
    }
  };
  
  const handleDeleteUser = async (userId: string) => {
    // Emin misiniz dialğını gösterdikten sonra
    const confirmed = window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.');
    
    if (!confirmed) return;
    
    try {
      setMessage(null);
      await deleteUserAccount(userId);
      
      // Admin işlemini kaydet
      if (userProfile) {
        await logAdminAction(userProfile.uid, 'delete_user', { targetUserId: userId });
      }
      
      // Kullanıcı listesini güncelle
      setUsers(prevUsers => prevUsers.filter(user => user.uid !== userId));
      
      setMessage({ type: 'success', text: 'Kullanıcı başarıyla silindi.' });
    } catch (err) {
      console.error("Kullanıcı silme hatası:", err);
      setMessage({ type: 'error', text: 'Kullanıcı silinirken bir hata oluştu.' });
    }
  };
  
  const handleCreateAnnouncement = async (announcementData: any) => {
    try {
      setMessage(null);
      
      const result = await addSystemAnnouncement({
        ...announcementData,
        startDate: new Date(announcementData.startDate),
        endDate: new Date(announcementData.endDate),
      });
      
      // Admin işlemini kaydet
      if (userProfile) {
        await logAdminAction(userProfile.uid, 'create_announcement', { announcementId: result });
      }
      
      setMessage({ type: 'success', text: 'Duyuru başarıyla oluşturuldu.' });
    } catch (err) {
      console.error("Duyuru oluşturma hatası:", err);
      setMessage({ type: 'error', text: 'Duyuru oluşturulurken bir hata oluştu.' });
    }
  };
  
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      // Arama terimi boşsa tüm kullanıcıları göster
      setSearchResults([]);
      return;
    }
    
    try {
      setLoading(true);
      
      // Arama kriterlerini belirle
      const criteria: any = {};
      
      // Email formatında ise email araması yap
      if (searchTerm.includes('@')) {
        criteria.email = searchTerm;
      } else {
        criteria.displayName = searchTerm;
      }
      
      // Kullanıcıları ara
      const result = await searchUsers(criteria, {
        pageSize: pagination.pageSize,
        startAfterDoc: undefined // null yerine undefined kullan
      });
      
      setSearchResults(result.users);
      lastDocRef.current = result.lastDoc;
      
      setPagination({
        ...pagination,
        currentPage: 1,
        hasMore: result.hasMore
      });
      
    } catch (err) {
      console.error("Kullanıcı arama hatası:", err);
      setMessage({ type: 'error', text: 'Kullanıcılar aranırken bir hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };
  
  const loadNextPage = async () => {
    if (!lastDocRef.current || !pagination.hasMore) return;
    
    try {
      setLoading(true);
      
      const criteria: any = {};
      if (searchTerm.includes('@')) {
        criteria.email = searchTerm;
      } else if (searchTerm) {
        criteria.displayName = searchTerm;
      }
      
      const result = await searchUsers(criteria, {
        pageSize: pagination.pageSize,
        startAfterDoc: lastDocRef.current
      });
      
      setSearchResults(prev => [...prev, ...result.users]);
      lastDocRef.current = result.lastDoc;
      
      setPagination({
        ...pagination,
        currentPage: pagination.currentPage + 1,
        hasMore: result.hasMore
      });
      
    } catch (err) {
      console.error("Sayfa yükleme hatası:", err);
      setMessage({ type: 'error', text: 'Daha fazla kullanıcı yüklenirken bir hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };
  
  const renderTabContent = () => {
    const displayUsers = searchResults.length > 0 ? searchResults : users;
    
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard stats={userStats} />;
      case 'users':
        return (
          <UserManagement 
            users={displayUsers} 
            onMakeAdmin={handleMakeAdmin}
            onMakePremium={handleMakePremium}
            onRemovePremium={handleRemovePremium}
            onSuspendUser={handleSuspendUser}
            onUnsuspendUser={handleUnsuspendUser}
            onDeleteUser={handleDeleteUser}
            onSearch={handleSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            pagination={pagination}
            loadNextPage={loadNextPage}
          />
        );
      case 'announcements':
        return <Announcements onCreateAnnouncement={handleCreateAnnouncement} />;
      case 'settings':
        return <Settings />;
      case 'analytics':
        return <Analytics stats={userStats} />;
      case 'logs':
        return <ActivityLogs />;
      case 'cities':
        return <CityManagement />;
      default:
        return <Dashboard stats={userStats} />;
    }
  };

  if (loading && !users.length) {
    return <div className={styles.loading}>Yükleniyor...</div>;
  }

  if (error && !users.length) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>
          <h3>Hata</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className={styles.retryButton}
          >
            Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminPanel}>
      <div className={styles.adminHeader}>
        <h1>Yönetici Paneli</h1>
        <p>Sistem ayarlarını yapılandırın, kullanıcıları yönetin ve istatistikleri inceleyin.</p>
      </div>

      {message && (
        <div className={styles[message.type]}>
          <p>{message.text}</p>
          <button 
            className={styles.closeButton} 
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      <div className={styles.adminContent}>
        <div className={styles.adminSidebar}>
          <div className={styles.adminNav}>
            <button 
              className={`${styles.navButton} ${activeTab === 'dashboard' ? styles.activeNavButton : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <MdDashboard className={styles.navIcon} />
              <span>Gösterge Paneli</span>
            </button>
            
            <button 
              className={`${styles.navButton} ${activeTab === 'users' ? styles.activeNavButton : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <MdPeople className={styles.navIcon} />
              <span>Kullanıcı Yönetimi</span>
            </button>
            
            <button 
              className={`${styles.navButton} ${activeTab === 'announcements' ? styles.activeNavButton : ''}`}
              onClick={() => setActiveTab('announcements')}
            >
              <MdAnnouncement className={styles.navIcon} />
              <span>Duyurular</span>
            </button>
            
            <button 
              className={`${styles.navButton} ${activeTab === 'cities' ? styles.activeNavButton : ''}`}
              onClick={() => setActiveTab('cities')}
            >
              <MdAppRegistration className={styles.navIcon} />
              <span>Şehir Yönetimi</span>
            </button>
            
            <button 
              className={`${styles.navButton} ${activeTab === 'analytics' ? styles.activeNavButton : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              <MdAnalytics className={styles.navIcon} />
              <span>İstatistikler</span>
            </button>
            
            <button 
              className={`${styles.navButton} ${activeTab === 'logs' ? styles.activeNavButton : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              <MdHistory className={styles.navIcon} />
              <span>İşlem Geçmişi</span>
            </button>
            
            <button 
              className={`${styles.navButton} ${activeTab === 'settings' ? styles.activeNavButton : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <MdSettings className={styles.navIcon} />
              <span>Ayarlar</span>
            </button>
          </div>
          
          <div className={styles.adminInfo}>
            <div className={styles.adminAvatar}>
              {userProfile?.photoURL ? (
                <img 
                  src={userProfile.photoURL} 
                  alt={userProfile.displayName || ''} // null yerine boş string kullan
                />
              ) : (
                <div className={styles.defaultAvatar}>
                  {userProfile?.displayName?.charAt(0) || 'A'} {/* Null check ekle */}
                </div>
              )}
            </div>
            <div className={styles.adminName}>
              {userProfile?.displayName}
            </div>
            <div className={styles.adminRole}>
              Yönetici
            </div>
          </div>
        </div>
        
        <div className={styles.adminMainContent}>
          {loading && <div className={styles.loadingOverlay}><div className={styles.loadingSpinner}></div></div>}
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
